import React, { useState, useEffect } from 'react';

import PlannerIcons from '../../components/shared/PlannerIcons';

export default function ProjectOverview({ project, safeNavigate }) {
  const [weather, setWeather] = useState({ days: [], status: 'Loading...', isReady: false, isHistorical: false });

  // NEW: Effect to fetch coordinates via Google, then fetch weather
  useEffect(() => {
    if (!project.location || project.location === 'TBD') {
         setWeather({ days: [], status: 'Location Needed', isReady: false, isHistorical: false });
         return;
    }
    
    if (window.google && window.google.maps && window.google.maps.Geocoder) {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ address: project.location }, (results, status) => {
            if (status === 'OK' && results[0]) {
                const lat = results[0].geometry.location.lat();
                const lng = results[0].geometry.location.lng();
                
                let apiUrl = '';
                let isHistorical = false;

                if (project.date && project.date !== 'TBD') {
                    const [year, month, day] = project.date.split('-');
                    const eventDate = new Date(year, month - 1, day);
                    
                    const today = new Date();
                    today.setHours(0,0,0,0);
                    
                    const diffDays = Math.ceil((eventDate - today) / (1000 * 60 * 60 * 24));

                    const formatDate = (dateObj) => {
                        const y = dateObj.getFullYear();
                        const m = String(dateObj.getMonth() + 1).padStart(2, '0');
                        const d = String(dateObj.getDate()).padStart(2, '0');
                        return `${y}-${m}-${d}`;
                    };

                    // Check if event is within 14 days (standard forecast range)
                    if (diffDays >= 0 && diffDays <= 14) {
                        const startStr = formatDate(eventDate);
                        const endObj = new Date(eventDate);
                        endObj.setDate(endObj.getDate() + 2);
                        const endStr = formatDate(endObj);
                        apiUrl = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}&daily=temperature_2m_max,weathercode&timezone=auto&start_date=${startStr}&end_date=${endStr}`;
                    } else if (diffDays < 0) {
                         // Past event
                         const startStr = formatDate(eventDate);
                         const endObj = new Date(eventDate);
                         endObj.setDate(endObj.getDate() + 2);
                         const endStr = formatDate(endObj);
                         apiUrl = `https://archive-api.open-meteo.com/v1/archive?latitude=${lat}&longitude=${lng}&daily=temperature_2m_max,weathercode&timezone=auto&start_date=${startStr}&end_date=${endStr}`;
                    } else {
                        // Future event > 14 days away, fetch historical data from past year
                        isHistorical = true;
                        const pastDate = new Date(eventDate);
                        pastDate.setFullYear(today.getFullYear() - 1); 
                        // Archive API data can be delayed by a few days. Ensure we're far enough in the past.
                        if (pastDate > new Date(Date.now() - 5 * 24 * 60 * 60 * 1000)) {
                           pastDate.setFullYear(today.getFullYear() - 2); 
                        }
                        
                        const startStr = formatDate(pastDate);
                        const endObj = new Date(pastDate);
                        endObj.setDate(endObj.getDate() + 2);
                        const endStr = formatDate(endObj);
                        apiUrl = `https://archive-api.open-meteo.com/v1/archive?latitude=${lat}&longitude=${lng}&daily=temperature_2m_max,weathercode&timezone=auto&start_date=${startStr}&end_date=${endStr}`;
                    }
                } else {
                    apiUrl = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}&daily=temperature_2m_max,weathercode&timezone=auto`;
                }
                
                fetch(apiUrl)
                    .then(res => res.json())
                    .then(data => {
                        if (data && data.daily && data.daily.time) {
                            const forecastDays = [];
                            const numDays = Math.min(3, data.daily.time.length);
                            for(let i = 0; i < numDays; i++) {
                                const tempC = data.daily.temperature_2m_max[i];
                                const tempF = Math.round((tempC * 9/5) + 32);
                                const code = data.daily.weathercode[i];
                                
                                const [y, m, d] = data.daily.time[i].split('-');
                                const dateObj = new Date(y, m - 1, d);
                                
                                if (isHistorical && project.date && project.date !== 'TBD') {
                                    // Align day name with the actual FUTURE event dates instead of past dates
                                    const [evY, evM, evD] = project.date.split('-');
                                    const futureDate = new Date(evY, evM - 1, evD);
                                    futureDate.setDate(futureDate.getDate() + i);
                                    dateObj.setTime(futureDate.getTime());
                                }

                                // Check if it's strictly "today"
                                const isToday = !isHistorical && dateObj.toDateString() === new Date().toDateString();
                                const dayName = i === 0 && isToday ? 'Today' : dateObj.toLocaleDateString('en-US', { weekday: 'short' });
                                
                                let icon = '⛅';
                                if (code <= 1) icon = '☀️';
                                else if (code <= 3) icon = '⛅';
                                else if (code <= 48) icon = '🌫️';
                                else if (code <= 67) icon = '🌧️';
                                else if (code <= 77) icon = '❄️';
                                else if (code <= 99) icon = '⛈️';
                                
                                forecastDays.push({ temp: `${tempF}°`, icon, dayName });
                            }
                            setWeather({ days: forecastDays, status: 'Ready', isReady: true, isHistorical });
                        } else {
                            setWeather({ days: [], status: 'Weather Data Unavailable', isReady: false, isHistorical: false });
                        }
                    })
                    .catch(() => setWeather({ days: [], status: 'Weather Unavailable', isReady: false, isHistorical: false }));
            } else {
                 setWeather({ days: [], status: 'Map Error', isReady: false, isHistorical: false });
            }
        });
    } else {
        setWeather({ days: [], status: 'Waiting for Maps...', isReady: false, isHistorical: false });
    }
  }, [project.location, project.date]);

  const daysUntil = project.date && project.date !== 'TBD' ? Math.ceil((new Date(project.date) - new Date()) / (1000 * 60 * 60 * 24)) : '--';
  const totalSpent = (project.budgetItems || []).reduce((sum, item) => sum + (Number(item.paid) || 0), 0);
  const budgetUsed = project.budgetGoal ? (totalSpent / project.budgetGoal) * 100 : 0;
  const rsvpAttending = (project.guests || []).filter(g => g.rsvp === 'Attending').length;

  return (
    <div className="space-y-6 md:space-y-8 animate-fade-in">
      {/* UPDATED: Changed from 3 columns to 4 columns to fit the new Weather widget */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        {/* Countdown */}
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm flex flex-col items-center justify-center text-center min-w-0">
          <div className="text-4xl md:text-5xl font-serif font-bold text-pink-600 mb-2 truncate w-full" title={daysUntil}>{daysUntil}</div>
          <p className="text-[10px] md:text-sm font-bold text-gray-400 uppercase tracking-widest truncate w-full">Days Until "I Do"</p>
        </div>

        {/* NEW: Weather Snapshot */}
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm min-w-0 flex flex-col justify-center">
          <div className="flex justify-between items-center mb-2">
             <span className="text-[10px] md:text-xs font-bold text-gray-400 uppercase tracking-wider truncate">3-Day Forecast</span>
             {!weather.isReady && <div className="w-8 h-8 rounded-full bg-orange-50 text-orange-600 flex items-center justify-center text-lg">🌤️</div>}
          </div>
          
          {weather.isReady && weather.days.length > 0 ? (
              <>
                  <div className="flex justify-between items-end gap-1 mt-2">
                      {weather.days.map((d, i) => (
                          <div key={i} className="flex flex-col items-center text-center flex-1">
                              <span className="text-xl md:text-2xl mb-1">{d.icon}</span>
                              <h3 className="text-sm md:text-base font-bold text-gray-900">{d.temp}</h3>
                              <p className="text-[9px] md:text-[10px] text-gray-500 font-medium uppercase tracking-wide">{d.dayName}</p>
                          </div>
                      ))}
                  </div>
                  {weather.isHistorical && (
                      <p className="text-[9px] text-gray-400 mt-3 text-center leading-tight border-t border-gray-100 pt-2" title="Event is too far out for a live forecast. Showing historical weather for these dates.">
                          *Showing historical weather for these dates (not today's weather)
                      </p>
                  )}
              </>
          ) : (
              <>
                  <h3 className="text-xl md:text-2xl font-bold text-gray-900 truncate mt-2">--</h3>
                  <p className="text-xs md:text-sm text-gray-500 mt-1 font-medium truncate">{weather.status}</p>
              </>
          )}
          {!weather.isHistorical && <div className="mt-2 h-1.5 w-full bg-transparent" />}
        </div>

        {/* Budget Snapshot */}
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm min-w-0">
          <div className="flex justify-between items-center mb-4">
             <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center"><PlannerIcons.Dollar /></div>
             <span className="text-[10px] md:text-xs font-bold text-gray-400 uppercase tracking-wider truncate">Budget</span>
          </div>
          <h3 className="text-xl md:text-2xl font-bold text-gray-900 truncate" title={`$${totalSpent.toLocaleString()}`}>${totalSpent.toLocaleString()}</h3>
          <p className="text-xs md:text-sm text-gray-500 mt-1 font-medium truncate" title={`of $${project.budgetGoal?.toLocaleString() || '0'} goal`}>of ${project.budgetGoal?.toLocaleString() || '0'} goal</p>
          <div className="mt-4 h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
             <div className={`h-full rounded-full ${budgetUsed > 100 ? 'bg-red-500' : 'bg-emerald-500'}`} style={{ width: `${Math.min(budgetUsed, 100)}%` }} />
          </div>
        </div>

        {/* Guest Snapshot */}
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm min-w-0">
          <div className="flex justify-between items-center mb-4">
             <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center"><PlannerIcons.Users /></div>
             <span className="text-[10px] md:text-xs font-bold text-gray-400 uppercase tracking-wider truncate">Guests</span>
          </div>
          <h3 className="text-xl md:text-2xl font-bold text-gray-900 truncate" title={rsvpAttending}>{rsvpAttending}</h3>
          <p className="text-xs md:text-sm text-gray-500 mt-1 font-medium truncate" title={`Attending (Goal: ${project.guestCountGoal || 0})`}>Attending (Goal: {project.guestCountGoal || 0})</p>
          <div className="mt-4 flex gap-1">
             <div className="h-1.5 bg-green-500 rounded-l-full" style={{ width: `${project.guestCountGoal ? (rsvpAttending/project.guestCountGoal)*100 : 0}%` }} />
             <div className="h-1.5 bg-gray-100 flex-1 rounded-r-full" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Next Tasks */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="px-4 md:px-6 py-4 border-b border-gray-100">
            <h3 className="font-bold text-gray-900">Upcoming Tasks</h3>
          </div>
          <div className="p-2">
            {(project.tasks || []).filter(t => !t.completed).slice(0, 5).map(task => (
              <div key={task.id} className="flex flex-wrap items-center gap-3 p-3 hover:bg-gray-50 rounded-lg transition-colors">
                <div className="w-5 h-5 rounded border-2 border-gray-300 flex-shrink-0" />
                <span className="text-gray-700 font-medium text-sm flex-1 min-w-[120px] truncate">{task.text}</span>
                <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wide bg-gray-100 px-2 py-1 rounded">{task.category}</span>
              </div>
            ))}
            {(!project.tasks || project.tasks.filter(t => !t.completed).length === 0) && (
                <p className="p-4 text-center text-sm text-gray-500 italic">No upcoming tasks.</p>
            )}
          </div>
        </div>

        {/* Vendor Team */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="px-4 md:px-6 py-4 border-b border-gray-100 flex justify-between items-center">
            <h3 className="font-bold text-gray-900">Vendor Team</h3>
          </div>
          <div className="p-4 space-y-3">
            {(project.vendors || []).map(vendor => (
              <div key={vendor.id} className="flex items-center justify-between p-3 border border-gray-100 rounded-lg hover:border-pink-200 transition-colors">
                <div className="min-w-0 pr-2">
                  {vendor.status.includes('Host-it!') && vendor.id && vendor.id.length > 15 ? (
                      <p 
                          onClick={(e) => { e.stopPropagation(); safeNavigate('vendorProfile', { vendorId: vendor.id }); }} 
                          className="font-bold text-sm text-pink-600 truncate cursor-pointer hover:underline"
                          title="View Storefront"
                      >
                          {vendor.name}
                      </p>
                  ) : (
                      <p className="font-bold text-sm text-gray-900 truncate">{vendor.name}</p>
                  )}
                  <p className="text-xs text-gray-500 font-medium">{vendor.category}</p>
                </div>
                <span className={`flex-shrink-0 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${vendor.status.includes('Host-it!') ? 'bg-pink-100 text-pink-700 border border-pink-200' : vendor.status === 'Booked' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                  {vendor.status}
                </span>
              </div>
            ))}
            {(!project.vendors || project.vendors.length === 0) && (
                <p className="text-center text-sm text-gray-500 italic">No vendors booked yet.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}